import { syncService } from '@/services/syncService';
import { SyncResult } from '@/services/offlineStorage';
import { offlineStorage } from '@/services/offlineStorage';

export interface BackgroundSyncConfig {
  enabled: boolean;
  interval: number; // en millisecondes
  retryAttempts: number;
  retryDelay: number; // en millisecondes
  minPendingChanges: number; // nombre minimum de changements pour déclencher une sync
  maxSyncDuration: number; // durée maximale d'une sync en millisecondes
}

export interface BackgroundSyncStatus {
  isRunning: boolean;
  lastSync: Date | null;
  nextSync: Date | null;
  pendingChanges: number;
  syncCount: number;
  errorCount: number;
  lastError: string | null;
}

export class BackgroundSyncService {
  private config: BackgroundSyncConfig;
  private status: BackgroundSyncStatus;
  private syncInterval: NodeJS.Timeout | null = null;
  private isSyncing = false;
  private syncTimeout: NodeJS.Timeout | null = null;

  constructor(config?: Partial<BackgroundSyncConfig>) {
    this.config = {
      enabled: true,
      interval: 5 * 60 * 1000, // 5 minutes par défaut
      retryAttempts: 3,
      retryDelay: 30 * 1000, // 30 secondes
      minPendingChanges: 1,
      maxSyncDuration: 2 * 60 * 1000, // 2 minutes
      ...config
    };

    this.status = {
      isRunning: false,
      lastSync: null,
      nextSync: null,
      pendingChanges: 0,
      syncCount: 0,
      errorCount: 0,
      lastError: null
    };

    this.initializeService();
  }

  private async initializeService(): Promise<void> {
    try {
      // Récupérer l'état depuis le stockage local
      const savedStatus = await offlineStorage.getData('sync_metadata', 'backgroundSyncStatus');
      if (savedStatus) {
        this.status = { ...this.status, ...savedStatus };
      }

      // Écouter les événements de connectivité
      window.addEventListener('online', this.handleOnline.bind(this));
      window.addEventListener('offline', this.handleOffline.bind(this));

      // Écouter les événements de changement de queue
      window.addEventListener('sync-queue-changed', this.handleQueueChanged.bind(this));

      // Écouter les événements de demande de synchronisation
      window.addEventListener('request-sync', this.handleSyncRequest.bind(this));

    } catch (error) {
      console.error('❌ Erreur initialisation service background sync:', error);
    }
  }

  // Démarrer la synchronisation automatique
  start(): void {
    if (!this.config.enabled || this.status.isRunning) {
      return;
    }
    this.status.isRunning = true;
    this.status.nextSync = new Date(Date.now() + this.config.interval);
    
    // Démarrer l'intervalle de synchronisation
    this.syncInterval = setInterval(() => {
      this.performBackgroundSync();
    }, this.config.interval);

    // Synchronisation immédiate si nécessaire
    this.checkAndSyncIfNeeded();

    this.saveStatus();
  }

  // Arrêter la synchronisation automatique
  stop(): void {
   
    this.status.isRunning = false;
    this.status.nextSync = null;

    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
      this.syncTimeout = null;
    }

    this.saveStatus();
  }

  // Vérifier et synchroniser si nécessaire
  private async checkAndSyncIfNeeded(): Promise<void> {
    try {
      // Ne pas synchroniser sans session (évite 401 en boucle sur login page)
      if (!localStorage.getItem('access_token')) {
        return;
      }

      const pendingChanges = await offlineStorage.getPendingChangesCount();
      this.status.pendingChanges = pendingChanges;

      if (pendingChanges >= this.config.minPendingChanges && navigator.onLine) {
        await this.performBackgroundSync();
      }
    } catch (error) {
      console.error('❌ Erreur vérification sync:', error);
    }
  }

  // Effectuer une synchronisation en arrière-plan
  private async performBackgroundSync(): Promise<SyncResult | void> {
    if (this.isSyncing || !navigator.onLine) {
      return;
    }

    if (!localStorage.getItem('access_token')) {
      return;
    }

    try {
      const pendingChanges = await offlineStorage.getPendingChangesCount();
      if (pendingChanges < this.config.minPendingChanges) {
        return;
      }

      this.isSyncing = true;
     
      // Définir un timeout pour éviter les sync trop longues
      this.syncTimeout = setTimeout(() => {
        console.warn('⏰ Timeout de synchronisation atteint');
        this.isSyncing = false;
      }, this.config.maxSyncDuration);

      // Effectuer la synchronisation
      const result = await syncService.syncData({
        skipConfirmation: true,
        syncType: 'incremental'
      });

      // Mettre à jour le statut
      this.status.lastSync = new Date();
      this.status.syncCount++;
      this.status.pendingChanges = await offlineStorage.getPendingChangesCount();
      this.status.lastError = null;

     
      // Émettre un événement de succès
      window.dispatchEvent(new CustomEvent('background-sync-success', { 
        detail: { result, timestamp: this.status.lastSync } 
      }));

      // Retourner le résultat
      return result;

    } catch (error) {
      console.error('❌ Erreur synchronisation automatique:', error);
      
      this.status.errorCount++;
      this.status.lastError = error instanceof Error ? error.message : 'Erreur inconnue';

      // Émettre un événement d'erreur
      window.dispatchEvent(new CustomEvent('background-sync-error', { 
        detail: { error, timestamp: new Date() } 
      }));

      // Programmer une nouvelle tentative
      this.scheduleRetrySync();

    } finally {
      this.isSyncing = false;
      
      if (this.syncTimeout) {
        clearTimeout(this.syncTimeout);
        this.syncTimeout = null;
      }

      this.saveStatus();
    }
  }

  // Programmer une nouvelle tentative de synchronisation
  private scheduleRetrySync(): void {
    if (this.status.errorCount >= this.config.retryAttempts) {
      console.warn('⚠️ Nombre maximum de tentatives atteint');
      return;
    }

   
    setTimeout(() => {
      this.performBackgroundSync();
    }, this.config.retryDelay);
  }

  // Gestionnaire d'événement en ligne
  private handleOnline(): void {
     
    // Vérifier si une synchronisation est nécessaire
    this.checkAndSyncIfNeeded();
  }

  // Gestionnaire d'événement hors ligne
  private handleOffline(): void {
    
    // Arrêter temporairement la synchronisation
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Gestionnaire de changement de queue
  private handleQueueChanged(): void {
    this.checkAndSyncIfNeeded();
  }

  // Gestionnaire de demande de synchronisation
  private handleSyncRequest(): void {
    if (navigator.onLine && !this.isSyncing) {
      this.performBackgroundSync();
    }
  }

  // Sauvegarder le statut
  private async saveStatus(): Promise<void> {
    try {
      await offlineStorage.saveData('sync_metadata', {
        key: 'backgroundSyncStatus',
        value: this.status
      });
    } catch (error) {
      console.error('❌ Erreur sauvegarde statut:', error);
    }
  }

  // Méthodes publiques
  getStatus(): BackgroundSyncStatus {
    return { ...this.status };
  }

  getConfig(): BackgroundSyncConfig {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<BackgroundSyncConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Redémarrer si nécessaire
    if (this.status.isRunning) {
      this.stop();
      this.start();
    }
  }

  async forceSync(): Promise<SyncResult> {
    if (this.isSyncing) {
      throw new Error('Une synchronisation est déjà en cours');
    }

    const result = await this.performBackgroundSync();
    
    if (!result) {
      // Si aucune synchronisation n'a été effectuée, retourner un résultat vide
      return {
        uploaded: 0,
        downloaded: 0,
        errors: [],
        conflicts: 0,
        duration: 0
      };
    }
    
    return result;
  }

  async getPendingChangesCount(): Promise<number> {
    const count = await offlineStorage.getPendingChangesCount();
    this.status.pendingChanges = count;
    return count;
  }

  // Méthodes de diagnostic
  async getDiagnostics(): Promise<{
    status: BackgroundSyncStatus;
    config: BackgroundSyncConfig;
    storageSize: number;
    pendingItems: any[];
  }> {
    const storageSize = await offlineStorage.getStorageSize();
    const pendingItems = await offlineStorage.getPendingSyncItems();

    return {
      status: this.getStatus(),
      config: this.getConfig(),
      storageSize,
      pendingItems
    };
  }

  // Nettoyer les données anciennes
  async cleanup(): Promise<void> {
    try {
      const cutoffDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 jours
      const pendingItems = await offlineStorage.getPendingSyncItems();
      
      const itemsToRemove = pendingItems.filter(item => 
        new Date(item.timestamp) < cutoffDate
      );

      for (const item of itemsToRemove) {
        await offlineStorage.removeSyncItem(item.id);
      }

    } catch (error) {
      console.error('❌ Erreur nettoyage:', error);
    }
  }
}

// Instance singleton
export const backgroundSyncService = new BackgroundSyncService();
